import { useEffect, useState, useRef } from "react";

import dayjs from "dayjs";
import { useFormik } from "formik";

import Col from "react-bootstrap/Col";

import { useLayout } from "@/../_metronic/layout/core";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { useDebounce } from "@/hooks/useDebounce";
import { OccupationPaginated } from "@/@types/sintegrada/OccupationPaginated";
import { Occupation } from "@/@types/sintegrada/Occupation";
import { InternalDepartmentParams } from "@/@types/sintegrada/InternalDepartmentParams";
import { exportExcel, extractTable } from "@/utils/exports";
import {
	occupationFilterInitialValues,
	TypeofOccupationFilterInitialValues,
} from "@/utils/initialValues/occupationFilter";
import { occupationFilterSchema } from "@/utils/Schemas/occupationFilterSchema";
import {
	dischargeTypeMessages,
	DischargeTypeEnum,
} from "@/utils/enums/sintegrada/occupation/DischargeTypeEnum";
import { InternalDepartmentTypeEnum } from "@/utils/enums/sintegrada/occupation/InternalDepartmentTypeEnum";
import {
	getInternalDepartmentResume,
	InternalDepartmentResume,
} from "@/services/sintegradaserver/occupation/getInternalDepartmentResume";
import { getDepartmentStabilizations } from "@/services/sintegradaserver/occupation/getDepartmentStabilizations";

import { getDepartmentObservations } from "@/services/sintegradaserver/occupation/getDepartmentObservations";

import { SearchInput } from "@/components/SearchInput";
import { PaginationLinks } from "@/components/PaginationLinks";
import { OccupationFilter } from "./OccupationFilter";
import { IndividualTable } from "./IndividualTable";
import { TotalTable } from "./TotalTable";
import { LoadingScreen } from "@/components/LoadingScreen";
import { getDepartmentHospitalizations } from "@/services/sintegradaserver/occupation/getDepartmentHospitalizations";
import { ReportBaseLayout } from "@/components/sintegrada/ReportBaseLayout";

export function OccupationReport() {
	const { setTitle } = useLayout();

	const totalTableRef = useRef<HTMLTableElement>(null);

	const [isLoading, setIsLoading] = useState(false);
	const [search, setSearch] = useState("");
	const debouncedSearch = useDebounce(search, 2000);
	const [paginatedOccupation, setPaginatedOccupation] = useState<OccupationPaginated>(
		{} as OccupationPaginated
	);
	const [internalDepartmentResume, setInternalDepartmentResume] = useState(
		{} as InternalDepartmentResume
	);
	const [pageNumber, setPageNumber] = useState(1);
	const [isLoadingExportExcel, setIsLoadingExportExcel] = useState(false);

	const { uf, ibgeCode, cnes } = useCurrentAccount();

	const occupationFilterValues = getSessionStorageOccuptaionFilterValues();

	const hasOccupationFilterValues = Boolean(Object.keys(occupationFilterValues).length);
	const hasTotalOccupations = Boolean(Object.keys(internalDepartmentResume).length);
	const hasOccupations = Boolean(paginatedOccupation.data?.length);

	function transformParams(values: TypeofOccupationFilterInitialValues) {
		const hospitalWardIds = values.hospitalWards
			.map((hospitalWard) => hospitalWard.value?.id)
			.filter((hospitalWardId) => Boolean(hospitalWardId));
		const riskClassificationId = values.riskClassification?.value?.id;

		return {
			uf,
			ibgeCode,
			cnes,
			startDate: dayjs(values.startDate)
				.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
				.startOf("minute")
				.format("YYYY-MM-DDTHH:mm:ssZ"),
			endDate: dayjs(values.endDate)
				.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
				.endOf("minute")
				.format("YYYY-MM-DDTHH:mm:ssZ"),
			hospitalWardIds,
			gender: values.gender?.value,
			minAge: values.minAge,
			maxAge: values.maxAge,
			riskClassificationId: riskClassificationId,
		};
	}

	async function fetchIndividualTableData(
		internalDepartmentType: InternalDepartmentTypeEnum,
		params: InternalDepartmentParams
	) {
		const fetchFunctions = {
			[InternalDepartmentTypeEnum.Internment]: () => getDepartmentHospitalizations(params),
			[InternalDepartmentTypeEnum.RedRoom]: () => getDepartmentStabilizations(params),
			[InternalDepartmentTypeEnum.Observation]: () => getDepartmentObservations(params),
		};

		const data = await fetchFunctions[internalDepartmentType as InternalDepartmentTypeEnum]();

		return data;
	}

	function getSessionStorageOccuptaionFilterValues() {
		const occupationFilterData = sessionStorage.getItem("occupationFilterValues") || "{}";
		const occupationFilterDataJSON = JSON.parse(
			occupationFilterData
		) as TypeofOccupationFilterInitialValues;

		return occupationFilterDataJSON;
	}

	const formik = useFormik({
		initialValues: occupationFilterInitialValues,
		validationSchema: occupationFilterSchema,
		async onSubmit(values) {
			const department = values.department?.value;

			const transformedParams = transformParams(values);

			if (values.individual) {
				const data = await fetchIndividualTableData(
					department.name as InternalDepartmentTypeEnum,
					{
						pageNumber: 1,
						...transformedParams,
					}
				);

				setPaginatedOccupation(data);

				sessionStorage.setItem("occupationFilterValues", JSON.stringify(values));

				return;
			}

			const internalDepartment = {
				[InternalDepartmentTypeEnum.Internment]: 1,
				[InternalDepartmentTypeEnum.Observation]: 2,
				[InternalDepartmentTypeEnum.RedRoom]: 3,
			};

			const data = await getInternalDepartmentResume({
				department:
					internalDepartment[values.department.value.name as InternalDepartmentTypeEnum],
				...transformedParams,
			});

			setInternalDepartmentResume(data);

			sessionStorage.setItem("occupationFilterValues", JSON.stringify(values));
		},
	});

	const disabledExportButton =
		(occupationFilterValues.individual && !hasOccupations) ||
		(!occupationFilterValues.individual && !hasTotalOccupations);

	async function handleGetIndividualTableData(pageNumber = 1, search = "") {
		setIsLoading(true);

		const internalDepartmentType = occupationFilterValues.department?.value
			.name as InternalDepartmentTypeEnum;
		const transformedParams = transformParams(occupationFilterValues);

		const data = await fetchIndividualTableData(internalDepartmentType, {
			pageNumber,
			search,
			...transformedParams,
		});

		setPaginatedOccupation(data);

		setIsLoading(false);
	}

	function transformIndividualTableToExportExcel(data: Occupation[]) {
		return data.map((occupation) => {
			const formattedAdmissionDate = dayjs(occupation.admissionDate).format(
				"DD/MM/YYYY hh:mm"
			);

			const birthdate = occupation.patient.birthdate;
			const formattedBirthdate = dayjs(birthdate).format("DD/MM/YYYY");

			const dischargeType = occupation.dischargeType
				? dischargeTypeMessages[occupation.dischargeType as DischargeTypeEnum]
				: "N/A";
			const dischargeDate = occupation.dischargeDate
				? dayjs(occupation.dischargeDate).format("DD/MM/YYYY")
				: "N/A";

			return {
				"Dt. Admissão": formattedAdmissionDate,
				"Nome do paciente": occupation.patient.name,
				"Dt. Nasc.": formattedBirthdate,
				Classificação: occupation.riskClassification?.name,
				Sexo: occupation.patient.gender,
				"Tipo alta": dischargeType,
				"Dt. alta": dischargeDate,
			};
		});
	}

	async function handleExportExcel() {
		setIsLoadingExportExcel(true);

		const internalDepartmentType = occupationFilterValues.department.value
			.name as InternalDepartmentTypeEnum;
		const isIndividual = occupationFilterValues.individual;

		const params = transformParams(occupationFilterValues);

		if (isIndividual) {
			const { data } = await fetchIndividualTableData(internalDepartmentType, {
				pageSize: paginatedOccupation.totalRecords,
				search,
				...params,
			});

			const transformedData = transformIndividualTableToExportExcel(data);

			exportExcel(transformedData, "Relatório de Ocupação Individual");

			setIsLoadingExportExcel(false);

			return;
		}

		const occupationData = extractTable(totalTableRef);

		exportExcel(occupationData, "Relatório de Ocupação Total");

		setIsLoadingExportExcel(false);
	}

	function handleChangePage(page: number) {
		setPageNumber(page);
	}

	useEffect(() => {
		if (hasOccupations) {
			handleGetIndividualTableData(pageNumber);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageNumber]);

	useEffect(() => {
		if (paginatedOccupation.pageNumber) {
			handleGetIndividualTableData(1, search);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedSearch]);

	useEffect(() => {
		setTitle("Relatório de ocupação");
		sessionStorage.removeItem("occupationFilterValues");

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<ReportBaseLayout>
			<LoadingScreen loading={formik.isSubmitting || isLoadingExportExcel} />

			<OccupationFilter
				formik={formik}
				handleExportExcel={handleExportExcel}
				disabledExportButton={disabledExportButton}
			/>

			{hasOccupationFilterValues && (
				<>
					{occupationFilterValues.individual ? (
						<>
							<Col lg={12} className="d-flex justify-content-end mb-8">
								<SearchInput
									placeholder="Buscar por nome ou dt. nasc"
									value={search}
									setValue={setSearch}
								/>
							</Col>

							<IndividualTable
								isLoading={isLoading}
								paginatedOccupation={paginatedOccupation}
							/>

							{hasOccupations && (
								<PaginationLinks
									itemsPerPage={paginatedOccupation.pageSize}
									pageNumber={paginatedOccupation.pageNumber}
									totalPages={paginatedOccupation.totalRecords}
									changeSelectedPage={handleChangePage}
								/>
							)}
						</>
					) : (
						<TotalTable
							isLoading={isLoading}
							ref={totalTableRef}
							occupationFilterValues={occupationFilterValues}
							internalDepartmentResume={internalDepartmentResume}
						/>
					)}
				</>
			)}
		</ReportBaseLayout>
	);
}
